import { languageDisplayCode, LanguagesEnum } from '@/api/enums';
import {
  ContentItemType,
  CourseContent,
  CourseContentType,
  CourseFormState,
  ModifiedResource,
  Resource,
  ResourceType,
} from '@/client/courses';
import { QuizType } from '@/client/quizzes/types';
import { Actions, Subjects } from '@/client/users';
import { SystemRoles } from '@/client/users/types/roles.type';
import { LoadingStateType } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { ReadConfirmationToggle } from '@/components/courses/ReadConfirmationToggle';
import { VideoFallbackLanguageSection } from '@/components/courses/VideoFallbackLanguageSection';
import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import { QuizEditorModal } from '@/components/quiz/editor/QuizEditorModal';
import { ContentSlideDeckModal } from '@/components/slides/admin/modals/ContentSlideDeckModal';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { LanguageTabs } from '@/ui/language-tabs';
import { FlexContainer } from '@/ui/styled-ui';
import {
  FeedbackIcon,
  getCourseContentTitle,
  GlobeIcon,
  modifyCourseResource,
  PdfIcon,
  QuizIcon,
  ScormIcon,
  SlideDeckIcon,
  TrashIcon,
  VideoIcon,
} from '@/utils/helpers';
import { without } from 'lodash';
import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ContentResourceModal } from './modals/ContentResourceModal';

const StyledFlexContainer = styled(FlexContainer)<{
  isFirst?: boolean;
  isLast?: boolean;
}>`
  position: relative;

  .line {
    position: absolute;
    left: var(--small-padding);
    top: 0;
    width: 2px;
    height: calc(100% + var(--medium-padding));
    background: var(--white-main);
    z-index: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  ${({ isFirst }) =>
    isFirst &&
    `.line {
        top: 50%;
        height: calc(50% + var(--medium-padding));
      }`}

  ${({ isLast }) =>
    isLast &&
    `.line {
        height: calc(50% + var(--medium-padding));
        top: -18px;
      }`}
`;

const StyledOrderIndicator = styled.div`
  background: var(--white-main);
  border-radius: 50%;
  display: flex;
  width: 26px;
  height: 26px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border: 2px solid var(--white-main);
  color: var(--black-main);
  font-size: var(--xxsmall-font-size);
  font-weight: 500;
  line-height: 150%;
  z-index: 1;
  position: relative;
  margin: 0;
  pointer-events: none;
`;

const StyledItem = styled(FlexContainer)`
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-dark);
  background: var(--white-main);
  padding: var(--default-padding);
  position: relative;
  margin-left: var(--default-padding);

  .title {
    font-weight: 600;
    margin-bottom: var(--small-padding);

    svg {
      margin-right: var(--small-padding);
    }
  }

  .languages {
    svg {
      margin-top: 3px;
    }
  }

  .actions {
    margin-top: var(--default-padding);

    button {
      height: 30px;
      padding: 0 28px;
      font-size: var(--xsmall-font-size);
      z-index: 999;

      .pi {
        font-size: var(--xsmall-font-size);
      }
    }

    .delete {
      display: flex;
      cursor: pointer;
      z-index: 999;

      &:hover {
        svg path {
          fill: var(--red-main);
        }
      }
    }
  }

  .default-video-dropdown,
  .read-confirmation {
    position: relative;
    z-index: 999;
    width: calc(100% + 2 * var(--default-padding));
    margin: var(--small-padding) calc(-1 * var(--default-padding))
      calc(-1 * var(--default-padding));
    padding: var(--small-padding) var(--default-padding);
    border-top: 1px solid var(--gray-dark);
  }
`;

type ContentItemProps = {
  index: number;
  values: CourseFormState;
  state?: LoadingStateType;
  isDisabled?: boolean;
  isSingle?: boolean;
  isDragging?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  setFieldValue: (field: string, value: any) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
};

type ContentItemKind =
  | 'presentation'
  | 'feedback'
  | 'quiz'
  | 'video'
  | 'pdf'
  | 'scorm'
  | 'unknown';

const resolveContentItemKind = (content: CourseContent): ContentItemKind => {
  if (content?.type === ContentItemType.PRESENTATION) return 'presentation';
  if (content?.quiz?.type === QuizType.FEEDBACK) return 'feedback';
  if (content?.quiz?.type === QuizType.TRADITIONAL) return 'quiz';

  switch (content?.resource?.type) {
    case ResourceType.VIDEO:
      return 'video';
    case ResourceType.PDF:
      return 'pdf';
    case ResourceType.SCORM:
      return 'scorm';
    default:
      return 'unknown';
  }
};

const CONTENT_KIND_LABEL_KEYS: Record<ContentItemKind, string> = {
  presentation: 'content.new.slidedeck',
  feedback: 'content.feedback',
  quiz: 'content.new.quiz',
  video: 'content.new.video',
  pdf: 'content.new.pdf',
  scorm: 'content.new.scorm',
  unknown: '',
};

const CONTENT_KIND_ICONS: Record<ContentItemKind, React.ReactNode> = {
  presentation: <SlideDeckIcon />,
  feedback: <FeedbackIcon />,
  quiz: <QuizIcon />,
  video: <VideoIcon />,
  pdf: <PdfIcon />,
  scorm: <ScormIcon />,
  unknown: null,
};

const getContentType = (content: CourseContent): CourseContentType => {
  if (content.type === CourseContentType.QUIZ) {
    return CourseContentType.QUIZ;
  }

  if (content.type === CourseContentType.PRESENTATION) {
    return CourseContentType.PRESENTATION;
  }

  return CourseContentType.RESOURCE;
};

export const ContentItem: React.FC<ContentItemProps> = ({
  index,
  values,
  state,
  isDisabled,
  isSingle,
  isDragging,
  isFirst,
  isLast,
  setFieldValue,
  setIsSubmitDisabled,
}) => {
  const { t } = useTranslation();
  const { isRestrictedByRole } = usePermission();
  const { id: courseId } = useParams();
  const { setDialogData } = useContext(DialogContext);
  const currentUser = useAppSelector(selectCurrentUser);
  const [isContentResourceModalVisible, setContentResourceModalVisible] =
    useState(false);
  const [isContentQuizModalVisible, setContentQuizModalVisible] =
    useState(false);
  const [isSlideDeckModalVisible, setSlideDeckModalVisible] = useState(false);

  // The write lands after the request resolves, so the row is located against
  // the latest contents rather than the array captured at render time.
  const courseContentsRef = useRef(values.courseContents);

  useLayoutEffect(() => {
    courseContentsRef.current = values.courseContents;
  }, [values.courseContents]);

  const applyResourceUpdate = useCallback(
    (resourceId: string, response: Resource) => {
      const updated = modifyCourseResource(response);
      if (!updated) return;

      const targetIndex = courseContentsRef.current.findIndex(
        (content: CourseContent) => content?.resource?.id === resourceId,
      );
      if (targetIndex === -1) return;

      setFieldValue(`courseContents.${targetIndex}.resource`, updated);
    },
    [setFieldValue],
  );

  const requiredTranslations = values.courseLanguages?.length
    ? values.courseLanguages.map(
        (languageCode: string) => languageDisplayCode[languageCode],
      )
    : [languageDisplayCode[LanguagesEnum.EN]];

  const translatedContentItem =
    values.courseContents[index]?.quiz ||
    values.courseContents[index]?.resource ||
    values.courseContents[index]?.presentation;

  const validDisplayCodes = (
    translatedContentItem?.fullyTranslatedLanguages || []
  ).map((languageCode: LanguagesEnum) => languageDisplayCode[languageCode]);

  const isVideoResource =
    values.courseContents[index]?.resource?.type === ResourceType.VIDEO;
  const fallbackOptions =
    isVideoResource && validDisplayCodes.length > 0
      ? requiredTranslations.filter((lang) => !validDisplayCodes.includes(lang))
      : [];
  const videoResource = isVideoResource
    ? values.courseContents[index]?.resource
    : undefined;
  const videoUploadedLanguages = videoResource?.fullyTranslatedLanguages ?? [];
  const isCustomFromScratchContent =
    !!values.customContent &&
    values.customizationMetadata?.originalContentId == null &&
    !values.cloneMetadata?.isClone;
  const showDefaultVideoDropdown =
    isVideoResource &&
    isCustomFromScratchContent &&
    videoUploadedLanguages.length > 1 &&
    fallbackOptions.length > 0;

  const getContentItemTitle = (content?: CourseContent): string => {
    if (!content) return '';

    const existingTitle = getCourseContentTitle(content);
    if (existingTitle) return existingTitle;

    const labelKey = CONTENT_KIND_LABEL_KEYS[resolveContentItemKind(content)];
    return labelKey ? t(labelKey) : '';
  };

  const getContentIcon = (content?: CourseContent) => {
    if (!content) return null;

    return CONTENT_KIND_ICONS[resolveContentItemKind(content)];
  };

  const handleEditItem = () => {
    const content = values.courseContents[index];
    const contentType = getContentType(content);

    switch (contentType) {
      case CourseContentType.PRESENTATION:
        setSlideDeckModalVisible(true);
        break;
      case CourseContentType.QUIZ:
        setContentQuizModalVisible(true);
        break;
      default:
        setContentResourceModalVisible(true);
        break;
    }
  };

  const handleDelete = () => {
    setDialogData({
      show: true,
      type: DialogType.WARNING,
      header: t('content.delete.item.confirm.title'),
      message: t('content.delete.item.confirm.text'),
      showDialogConfirmation: false,
      confirmButtonLabel: t('content.delete.item.confirm.action'),
      onAccept: () =>
        setFieldValue(
          'courseContents',
          without(values.courseContents, values.courseContents[index]),
        ),
    });
  };

  const getActionButtonLabel = () => {
    const content = values.courseContents[index];
    const contentType = getContentType(content);
    const hasContent = content?.quiz?.id || content?.resource?.id;

    if (content?.type === ContentItemType.PRESENTATION) {
      return t('generic.edit');
    }

    if (contentType === CourseContentType.QUIZ) {
      return hasContent ? t('content.edit.quiz') : t('content.create.quiz');
    }

    return hasContent ? t('generic.edit') : t('content.upload');
  };

  const isEditRestricted = isRestrictedByRole(Actions.UPDATE, Subjects.COURSES);

  const isFeedbackQuiz =
    values.courseContents[index]?.quiz?.type === QuizType.FEEDBACK;
  const isSuperAdminOrDeveloper =
    currentUser?.role?.code === SystemRoles.DEVELOPER ||
    currentUser?.role?.code === SystemRoles.SUPER_ADMIN;
  const showEditAction = !isFeedbackQuiz;
  const showDeleteAction =
    !isDisabled && (!isFeedbackQuiz || isSuperAdminOrDeveloper);
  const hasCourseLanguages =
    Array.isArray(values.courseLanguages) && values.courseLanguages.length > 0;

  return (
    <>
      <StyledFlexContainer direction="row" isFirst={isFirst} isLast={isLast}>
        {!isSingle && (
          <div className="line" style={{ opacity: isDragging ? 0 : 1 }} />
        )}
        <StyledOrderIndicator>{index + 1}</StyledOrderIndicator>

        <StyledItem
          direction="column"
          gap="var(--xxxsmall-padding)"
          width="100%"
        >
          {/* Title */}
          <FlexContainer
            justify="flex-start"
            align="center"
            className="title notranslate"
            translate="no"
            data-translate="no"
          >
            {getContentIcon(values.courseContents[index])}
            {getContentItemTitle(values.courseContents[index])}
          </FlexContainer>

          {/* Languages */}
          <FlexContainer
            gap="var(--semi-xsmall-padding)"
            justify="flex-start"
            align="flex-center"
            className="languages"
          >
            {hasCourseLanguages && <GlobeIcon />}
            <FlexContainer
              gap="var(--semi-xsmall-padding)"
              justify="flex-start"
              wrap="wrap"
            >
              <LanguageTabs
                options={values.courseLanguages || []}
                requiredOptions={requiredTranslations}
                validOptions={validDisplayCodes}
                fallbackOptions={fallbackOptions}
                isDisabled={isDisabled}
                limitVisible={5}
                resetVisibility={isDragging}
              />
            </FlexContainer>
          </FlexContainer>

          {/* Actions */}
          {(showEditAction || showDeleteAction) && (
            <FlexContainer
              justify="space-between"
              align="center"
              gap="var(--semi-xsmall-padding)"
              wrap="wrap"
              className="actions"
            >
              {showEditAction && hasCourseLanguages && (
                <ViewOnlyRestrictionWhen restricted={isEditRestricted}>
                  <AppButton
                    severity="secondary"
                    type="outlined"
                    icon={
                      values.courseContents[index]?.type !==
                        CourseContentType.QUIZ &&
                      values.courseContents[index]?.type !==
                        ContentItemType.PRESENTATION &&
                      'pi pi-upload'
                    }
                    iconPos="right"
                    label={getActionButtonLabel()}
                    state={state}
                    onClick={isEditRestricted ? undefined : handleEditItem}
                    isDisabled={isDisabled || isEditRestricted}
                  />
                </ViewOnlyRestrictionWhen>
              )}
              {showDeleteAction && (
                <div className="delete ml-auto" onClick={handleDelete}>
                  <TrashIcon />
                </div>
              )}
            </FlexContainer>
          )}

          {showDefaultVideoDropdown && (
            <VideoFallbackLanguageSection
              courseId={courseId}
              resource={videoResource as ModifiedResource | undefined}
              uploadedLanguages={videoUploadedLanguages}
              isDisabled={isDisabled}
              onUpdated={applyResourceUpdate}
            />
          )}

          {values.courseContents[index]?.resource?.type ===
            ResourceType.PDF && (
            <ReadConfirmationToggle
              courseId={courseId}
              resource={
                values.courseContents[index]?.resource as
                  | ModifiedResource
                  | undefined
              }
              tooltipTarget={`read-confirmation-info-${index}`}
              isDisabled={isDisabled}
              onUpdated={applyResourceUpdate}
            />
          )}
        </StyledItem>
      </StyledFlexContainer>

      <ContentResourceModal
        index={index}
        values={values}
        visible={isContentResourceModalVisible}
        isDisabled={!!isDisabled}
        setFieldValue={setFieldValue}
        setIsSubmitDisabled={setIsSubmitDisabled}
        onHide={() => setContentResourceModalVisible(false)}
      />

      <QuizEditorModal
        index={index}
        values={values}
        visible={isContentQuizModalVisible}
        isDisabled={!!isDisabled}
        setFieldValue={setFieldValue}
        setIsSubmitDisabled={setIsSubmitDisabled}
        onHide={() => setContentQuizModalVisible(false)}
      />

      <ContentSlideDeckModal
        index={index}
        values={values}
        setFieldValue={setFieldValue}
        visible={isSlideDeckModalVisible}
        onHide={() => setSlideDeckModalVisible(false)}
      />
    </>
  );
};
